// Port of WifiBadge() + the AnimatedVisibility wrapper in MainActivity.kt.
// Two responsibilities:
//   1. Render the pill (icon + label, color = green | red).
//   2. Animate slide-up/fade-in on mount and out on unmount, via a CSS
//      class that targets the `wifi-badge-*` keyframes declared in index.css.
//
// The parent (App.tsx) controls *when* the badge is mounted (only during the
// 3.5s window after an online/offline transition). This component only knows
// "I'm being rendered, so slide myself in."

interface Props {
  isOnline: boolean
}

// Inline SVG so the webplayer doesn't pull lucide-react. Stroke-only paths
// match Lucide's wifi / wifi-off shapes — readable from across the room.
function WifiIcon({ online, color }: { online: boolean; color: string }) {
  if (online) {
    return (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={color}
           strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M5 12.55a11 11 0 0 1 14.08 0" />
        <path d="M1.42 9a16 16 0 0 1 21.16 0" />
        <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
        <line x1="12" y1="20" x2="12.01" y2="20" />
      </svg>
    )
  }
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={color}
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="1" y1="1" x2="23" y2="23" />
      <path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55" />
      <path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39" />
      <path d="M10.71 5.05A16 16 0 0 1 22.58 9" />
      <path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88" />
      <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
      <line x1="12" y1="20" x2="12.01" y2="20" />
    </svg>
  )
}

export default function WifiBadge({ isOnline }: Props) {
  const color = isOnline ? 'var(--status-online)' : 'var(--status-offline)'
  const label = isOnline ? 'Connected' : 'No connection'
  return (
    <div
      className="wifi-badge inline-flex items-center gap-1.5 rounded-full px-3 py-1.5"
      // Neutral canvas scrim (matches --kiosk-bg) so the badge reads over content.
      style={{ background: 'rgba(10, 10, 10, 0.88)' }}
    >
      <WifiIcon online={isOnline} color={color} />
      <span className="text-[13px] font-semibold" style={{ color }}>{label}</span>
    </div>
  )
}
